import type React from 'react';

import { useTranslation } from 'react-i18next';

import type { SalesforceNormalizedEntity } from '../../../../../domain/salesforceActivity';
import { AccountDropdownButton } from '../AccountDropdownButton/AccountDropdown';

import {
  StyledAccountDropdown,
  StyledAccountDropdownContainer,
  StyledAccountName,
  StyledAccountOption,
  StyledAccountOptionCount,
  StyledAccountOptionHeaded,
  StyledAccountOptionInfo,
} from './styles';
import { useAccountDropdown } from './useAccountDropdown';

type Props = {
  entities: readonly SalesforceNormalizedEntity[];
  defaultEntity: SalesforceNormalizedEntity;
  onEntityChange: (entity: SalesforceNormalizedEntity) => void;
};

export const AccountDropdown: React.FC<Props> = ({ entities, defaultEntity, onEntityChange }) => {
  const { t } = useTranslation();
  const {
    isOpened,
    handleOpenDropdown,
    handleCloseDropdown,
    selectedEntity,
    searchText,
    handleSearchText,
    filteredEntities,
    handleSelectEntity,
  } = useAccountDropdown<SalesforceNormalizedEntity>({
    entities,
    filterKey: 'name',
    defaultEntity,
    onEntityChange,
  });

  return (
    <StyledAccountDropdownContainer data-automation="SalesforceActivityAccountFilter">
      <AccountDropdownButton
        onClick={handleOpenDropdown}
        isSelected={!!selectedEntity}
        isActive={isOpened}
        isDisabled={entities.length === 0}
        dataAutomation="SalesforceActivityAccountFilterButton"
      >
        {selectedEntity?.name ?? ''}
      </AccountDropdownButton>
      <StyledAccountDropdown
        isOpened={isOpened}
        onClose={handleCloseDropdown}
        onCloseFinish={() => handleSearchText('')}
        searchProps={{
          placeholder: t('common.search') || '',
          value: searchText,
          onChange: (e) => handleSearchText(e.target.value),
        }}
        data-automation="SalesforceActivityAccountFilterDropdown"
        maxHeight={284}
      >
        {filteredEntities.map((entity) => (
          <StyledAccountOption
            key={entity.id}
            isSelected={selectedEntity?.id === entity.id}
            onClick={() => handleSelectEntity(entity)}
          >
            <StyledAccountOptionHeaded>
              <StyledAccountName>{entity.name}</StyledAccountName>
              <StyledAccountOptionCount>{entity.taskCount}</StyledAccountOptionCount>
            </StyledAccountOptionHeaded>
            <StyledAccountOptionInfo>
              {t(`company.salesforce_activity.account-dropdown.sub-type.${entity.subType}`)}{' '}
              {entity.description ? `| ${entity.description}` : ''}
            </StyledAccountOptionInfo>
          </StyledAccountOption>
        ))}
      </StyledAccountDropdown>
    </StyledAccountDropdownContainer>
  );
};
